import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomInput,
  CustomPassword,
  successDialog,
  Text,
  useText,
} from '@src/components';
import { useCallback, useEffect, useState } from 'react';
import formRule from '@src/utils/formRule';
import { Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { changeUserPassword } from '@src/store/slice/authSlice';
import { passwordRegex } from '@src/utils/utils';

const PasswordInformation = () => {
  const { currentUser } = useSelector((state) => state?.user);
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const [passwordError, setPasswordError] = useState({
    visible: false,
    messageList: [],
    isValid: false,
    errorList: [],
  });

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const getMessageList = (data) => {
    const messageList = [];
    data?.forEach((item) => {
      messageList?.push({ type: 'error', message: item?.text });
    });
    return messageList;
  };

  useEffect(() => {
    (async () => {
      setPasswordError({
        visible: true,
        messageList: getMessageList(passwordRegex),
        isValid: false,
        errorList: passwordRegex,
      });
    })();
  }, [dispatch]);

  const onCancel = useCallback(() => {
    form.setFieldsValue({
      userName: currentUser?.userName,
      currentPassword: '',
      newPassword: '',
      reNewPassword: '',
    });
  }, [currentUser, form]);

  const onFinish = useCallback(
    async (values) => {
      const body = {
        currentPassword: values?.currentPassword,
        newPassword: values?.newPassword,
      };

      const action = await dispatch(changeUserPassword(body));
      if (changeUserPassword.fulfilled.match(action)) {
        successDialog({
          title: <Text t="success" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
        });
        onCancel();
        const message = getMessageList(passwordError.errorList);
        setPasswordError({ ...passwordError, messageList: message });
      }
    },
    [passwordError, dispatch, onCancel],
  );

  const regexErrorItemResult = (item = {}, value = '') => {
    const regex = new RegExp(item?.regExp);
    if (regex.test(value)) {
      return { listItem: { type: 'success', message: item?.text }, validation: false };
    } else {
      return { listItem: { type: 'error', message: item?.text }, validation: true };
    }
  };

  const resultErrorList = (list, value) => {
    const messageList = [];
    const validations = [];
    list?.forEach((item = { regExp: '', text: '' }) => {
      const { listItem, validation } = regexErrorItemResult(item, value);
      messageList.push(listItem);
      validations.push(validation);
    });

    return { messageList, validations };
  };

  const onChange = (e) => {
    const value = e?.target?.value;

    const { messageList, validations } = resultErrorList(passwordError.errorList, value);

    setPasswordError({
      ...passwordError,
      visible: messageList.length > 0,
      messageList,
      isValid: !validations.includes(true),
    });
  };

  return (
    <div>
      <div className="header-content">
        <h4>
          <Text t="passwordInfo" />
        </h4>
      </div>

      <div className="personal-information-form">
        <div className="personal-form">
          <CustomForm
            name="passwordInformation"
            onFinish={onFinish}
            form={form}
            initialValues={{
              userName: currentUser?.userName,
            }}
            awaitLoading={!currentUser?.id}
            autoComplete="off"
            layout={'vertical'}
          >
            <div className="row ">
              <div className="col-md-6">
                <CustomFormItem
                  label={<Text t="loginUsernameInputForgotPassword" />}
                  name="userName"
                >
                  <CustomInput
                    id="myProfile-passwordInformation-userName-input"
                    disabled
                    placeholder={useText('loginUsernameInputForgotPassword')}
                    maxLength={50}
                  />
                </CustomFormItem>
              </div>

              <div className="col-md-6">
                <CustomFormItem
                  label={<Text t="currentPassword" />}
                  name="currentPassword"
                  rules={[
                    { required: true, max: 20, message: <Text t="loginPasswordInputRequired" /> },
                  ]}
                >
                  <CustomPassword
                    id="myProfile-passwordInformation-currentPassword-password-input"
                    data-testid="currentPassword"
                    placeholder={useText('loginPasswordInput')}
                    maxLength={20}
                  />
                </CustomFormItem>
              </div>
            </div>

            <div className="row ">
              <div className="col-md-6">
                <CustomFormItem
                  label={<Text t="newPassword" />}
                  name="newPassword"
                  rules={[
                    { required: true, max: 20, message: <Text t="loginPasswordInputRequired" /> },
                  ]}
                >
                  <CustomPassword
                    id="myProfile-passwordInformation-newPassword-password-input"
                    placeholder={useText('loginPasswordInputRequired')}
                    onChange={onChange}
                    autoComplete="off"
                    data-testid="newPassword"
                    maxLength={20}
                  />
                </CustomFormItem>
              </div>

              <div className="col-md-6">
                <CustomFormItem
                  label={<Text t="againNewPassword" />}
                  name="reNewPassword"
                  rules={[
                    { required: true, max: 20, message: <Text t="loginPasswordInputRequired" /> },
                    (ruleForm) =>
                      formRule.formStringMatching(
                        ruleForm,
                        'newPassword',
                        <Text t="twoPasswordDontMatch" />,
                      ),
                  ]}
                >
                  <CustomPassword
                    id="myProfile-passwordInformation-reNewPassword-password-input"
                    data-testid="reNewPassword"
                    placeholder={useText('againPasswordInput')}
                    autoComplete="off"
                    maxLength={20}
                  />
                </CustomFormItem>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <ul className="password-information-info">
                  <li>
                    <Text t="yourPassword" />
                  </li>
                  {passwordError.messageList?.map((item, index) => {
                    return (
                      <li
                        style={
                          item?.type === 'success' ? { color: '#689b00' } : { color: '#f72717' }
                        }
                        key={`profile-pass-error-list-${item?.message}`}
                      >
                        {item?.message}
                      </li>
                    );
                  })}
                  <li>
                    <Text t="mustContain" />
                  </li>
                </ul>
              </div>
            </div>

            <CustomFormItem className="custom-form-footer">
              <CustomButton id="myProfile-passwordInformation-custom-btn-cancel" className="custom-btn-cancel" onClick={onCancel}>
                <span className="submit">
                  <Text t="giveUp" />
                </span>
              </CustomButton>
              <CustomButton
                id="myProfile-passwordInformation-custom-btn-primary"
                data-testid="submit"
                className={passwordError.isValid ? "custom-btn-primary" : "custom-btn-not-valid custom-btn-primary"}
                type="primary"
                htmlType="submit"
              >
                <span className="submit">
                  <Text t="passwordUpdateBtn" />
                </span>
              </CustomButton>
            </CustomFormItem>
          </CustomForm>
        </div>
      </div>
    </div>
  );
};

export default PasswordInformation;
